import { Route } from "../../../utils/models/Route";
import { distance as turfDistance } from "@turf/turf";
import { LineStringFeature } from "../../../utils/models/LineStringFeature";

/** The fewest and most days a plan can be split into (matches hike-planner). */
export const MIN_DAYS = 1;
export const MAX_DAYS = 30;

/** A drop closer than this to the line counts as on it — no detour. */
export const ON_TRAIL_M = 30;

/** A walking line with the distance along it at each of its coordinates. */
export interface MeasuredLine {
  coordinates: number[][];
  cumDist: number[];
}

/**
 * Where one day ends and the next begins along a line, plus an optional
 * detour off it (an overnight stop away from the trail). `detourLine` runs
 * from the line out to `offTrailPoint`; the following day walks it reversed.
 */
export interface DayBoundary {
  distanceM: number;
  offTrailPoint: number[] | null;
  detourLine: LineStringFeature | null;
}

export function cumulativeDistances(coordinates: number[][]): number[] {
  const cumDist = [0];
  for (let i = 1; i < coordinates.length; i++) {
    const prev = coordinates[i - 1];
    const cur = coordinates[i];
    cumDist.push(cumDist[i - 1] + turfDistance([prev[0], prev[1]], [cur[0], cur[1]], { units: 'meters' }));
  }
  return cumDist;
}

function interpolateCoord(a: number[], b: number[], t: number): number[] {
  return [
    a[0] + (b[0] - a[0]) * t,
    a[1] + (b[1] - a[1]) * t,
    (a[2] ?? 0) + ((b[2] ?? 0) - (a[2] ?? 0)) * t,
  ];
}

/** Coordinate (with interpolated elevation) at `distance` metres along coordinates/cumDist. */
export function pointAtDistance(coordinates: number[][], cumDist: number[], distance: number): number[] {
  if (distance <= 0) return coordinates[0];
  const total = cumDist[cumDist.length - 1];
  if (distance >= total) return coordinates[coordinates.length - 1];

  for (let i = 1; i < cumDist.length; i++) {
    if (cumDist[i] >= distance) {
      const segStart = cumDist[i - 1];
      const segLen = cumDist[i] - segStart;
      const t = segLen === 0 ? 0 : (distance - segStart) / segLen;
      return interpolateCoord(coordinates[i - 1], coordinates[i], t);
    }
  }
  return coordinates[coordinates.length - 1];
}

export function sliceByDistance(coordinates: number[][], cumDist: number[], fromDist: number, toDist: number): number[][] {
  const result: number[][] = [pointAtDistance(coordinates, cumDist, fromDist)];
  for (let i = 0; i < coordinates.length; i++) {
    if (cumDist[i] > fromDist && cumDist[i] < toDist) {
      result.push(coordinates[i]);
    }
  }
  result.push(pointAtDistance(coordinates, cumDist, toDist));
  return result;
}

/**
 * Nearest VERTEX on a line to an arbitrary point (a vertex search, not a
 * segment projection — fine for routed lines, whose vertices are dense; see
 * nearestAlongLine in services/day-flags.ts for the projection).
 */
export function nearestOnTrail(
  coordinates: number[][],
  cumDist: number[],
  lon: number,
  lat: number
): { point: number[]; distanceM: number; offTrailM: number } {
  let bestIndex = 0;
  let bestD = Infinity;
  for (let i = 0; i < coordinates.length; i++) {
    const d = turfDistance([lon, lat], [coordinates[i][0], coordinates[i][1]], { units: 'meters' });
    if (d < bestD) {
      bestD = d;
      bestIndex = i;
    }
  }
  return { point: coordinates[bestIndex], distanceM: cumDist[bestIndex], offTrailM: bestD };
}

/**
 * Where a route branches off/rejoins the main line — its first marker if it
 * has one, else the first coordinate of its first lineString. Used to regroup
 * alt/spur legs onto whichever day their geometry now sits closest to after
 * the days are re-spaced, rather than leaving them under a day number that no
 * longer means anything geographically.
 */
export function routeAnchorCoord(route: Route): number[] | null {
  const markerCoord = route.markers?.[0]?.markerGeometry?.coordinates;
  if (markerCoord && markerCoord.length >= 2) return markerCoord;
  const lineCoord = route.lineStrings?.[0]?.lineStringGeometry?.coordinates?.[0];
  return lineCoord ?? null;
}

/**
 * Which day (1..dayCount) a distance along the line falls into, given the day
 * boundaries in effect. `boundaries[d]` is where day `d` ends / day `d + 1`
 * begins, so a distance is in day `d` when it's at or before `boundaries[d]`,
 * else it falls through to later days (last day if it's past every boundary).
 */
export function dayNumberForDistance(
  distanceM: number,
  boundaries: { [day: number]: DayBoundary },
  dayCount: number
): number {
  for (let day = 1; day < dayCount; day++) {
    const boundary = boundaries[day];
    if (boundary && distanceM <= boundary.distanceM) return day;
  }
  return dayCount;
}
